import { useEffect, useState, type CSSProperties } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Seo } from '../components/Seo';
import { SubpageShell } from '../components/layout/SubpageShell';
import { HeroBlobs } from '../components/subpage/blocks';
import { primaryButton, secondaryButton } from '../components/subpage/styles';
import { Icon } from '../components/ui/Icon';
import { notFoundPage as copy } from '../content/pages/bulunamadi';
import { notFoundSeo } from '../content/seo';

/** Kaydırmasız açılan sayfada öğeler yukarıdan aşağıya sırayla belirir (`.zh-reveal` gecikmesi). */
const delay = (step: number): CSSProperties => ({ transitionDelay: `${step * 0.07}s` });

/**
 * Bulunamayan adres: site kabuğu (menü + footer) içinde, alt sayfa hero'suyla aynı dilde. Büyük gradient
 * "404", kısa açıklama, iki düğme ve sık gidilen sayfalar; ziyaretçi çıkmaz sokakta kalmasın.
 * Arama motoruna `noindex` söylenir (`notFoundSeo`), aksi hâlde her yanlış adres "sayfa" sayılır. Build'de
 * `404.html` olarak ön-çizilir (sunucu bilinmeyen adreste bunu döner); istenen adres rozeti bu yüzden
 * bağlandıktan sonra dolar: ön-çizimdeki sahte yol ile tarayıcıdaki gerçek yol farklı olunca React HTML'i
 * atıp yeniden çiziyordu.
 */
export default function NotFound() {
  const { pathname, search } = useLocation();
  const [shownPath, setShownPath] = useState<string | null>(null);
  useEffect(() => setShownPath(pathname + search), [pathname, search]);

  return (
    <SubpageShell pageKey="bulunamadi">
      <Seo {...notFoundSeo} />
      <section className="relative overflow-hidden px-4 pb-20 pt-6 sm:px-6 lg:pb-[112px] lg:pt-12">
        <HeroBlobs accent="#45c0ee" opacity={0.2} />

        <div className="relative mx-auto flex max-w-[760px] flex-col items-center text-center">
          {/* Büyük rakam: gradient yazı, arkasında yumuşak parıltı. Dekoratif; başlık aşağıdaki h1. */}
          <div aria-hidden="true" className="zh-reveal relative" style={delay(0)}>
            <span className="absolute left-1/2 top-1/2 h-[200px] w-[380px] -translate-x-1/2 -translate-y-1/2 rounded-full bg-[radial-gradient(closest-side,rgba(69,192,238,0.2),rgba(0,0,0,0))] blur-2xl" />
            <span className="zh-grad-text relative block font-display text-[128px] font-light leading-none tracking-[-0.06em] sm:text-[176px]">
              {copy.code}
            </span>
          </div>

          <span className="zh-reveal mt-1 text-[13px] font-bold uppercase tracking-[0.16em] text-brand-300" style={delay(1)}>
            {copy.eyebrow}
          </span>
          <h1
            className="zh-reveal m-0 mt-4 font-display text-[32px] font-semibold leading-[1.1] tracking-[-0.03em] text-white sm:text-[44px]"
            style={delay(2)}
          >
            {copy.title}
          </h1>
          <p className="zh-reveal m-0 mt-5 max-w-[560px] text-[16px] leading-[1.7] text-ink-soft sm:text-[17px]" style={delay(3)}>
            {copy.description}
          </p>

          {/* İstenen adres rozeti; ilk çizimde boş ve görünmez (ön-çizimle aynı), bağlanınca dolar. */}
          <p
            className={`zh-reveal m-0 mt-6 inline-flex max-w-full items-center gap-2 rounded-full border border-[rgba(100,140,220,0.22)] bg-white/[0.03] px-3.5 py-2 font-mono text-[13px] text-ink-subdued${shownPath ? '' : ' invisible'}`}
            style={delay(4)}
          >
            <span className="shrink-0">{copy.pathLabel}:</span>
            <span className="truncate text-ink-soft">{shownPath}</span>
          </p>

          <div className="zh-reveal mt-9 flex flex-wrap items-center justify-center gap-3" style={delay(5)}>
            <Link to={copy.primary.href} className={primaryButton}>
              {copy.primary.label}
              <Icon name="arrowRight" size={16} strokeWidth={2} />
            </Link>
            <Link to={copy.secondary.href} className={secondaryButton}>
              {copy.secondary.label}
            </Link>
          </div>
        </div>

        <nav aria-label={copy.suggestionsTitle} className="zh-reveal relative mx-auto mt-16 w-full max-w-[960px]" style={delay(6)}>
          <h2 className="m-0 mb-4 text-center text-[12px] font-bold uppercase tracking-[0.16em] text-ink-subdued">
            {copy.suggestionsTitle}
          </h2>
          <ul className="m-0 grid list-none gap-3 p-0 sm:grid-cols-2 lg:grid-cols-4">
            {copy.suggestions.map((item) => (
              <li key={item.href}>
                <Link
                  to={item.href}
                  className="group flex h-full flex-col gap-1.5 rounded-[16px] border border-brand-300/[0.14] bg-[color-mix(in_srgb,white_3%,var(--surface-bg-subdued))] p-4 text-left no-underline transition-colors hover:border-brand-300/50"
                >
                  <span className="flex items-center justify-between gap-2 text-[15px] font-bold text-white">
                    {item.label}
                    <span className="text-brand-300 transition-transform group-hover:translate-x-0.5">
                      <Icon name="arrowRight" size={16} strokeWidth={2} />
                    </span>
                  </span>
                  <span className="text-[13px] leading-[1.5] text-ink-subdued">{item.desc}</span>
                </Link>
              </li>
            ))}
          </ul>
        </nav>
      </section>
    </SubpageShell>
  );
}
